import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { PackingSlipTemplatesContext } from "../../common/print-batch-modal";

export const FulfillmentPackingSlipTemplateCard = memo(
  function FulfillmentPackingSlipTemplateCard({
    fulfillmentGroups,
    updateRows,
    everythingDisabled,
    isEditingLocked,
  }: {
    fulfillmentGroups: readonly FulfillmentGroupData[];
    updateRows: (updates: FulfillmentGroupUpdate[]) => void;
    everythingDisabled: boolean;
    isEditingLocked: boolean;
  }) {
    const team = useRequiredContext(TeamContext);

    // Use the context instead of loading templates directly
    const templates = useContext(PackingSlipTemplatesContext);

    // Determine the currently selected template ID for the fulfillment group(s)
    const { selectedTemplateId, mixedTemplates, mixedSizes } = useMemo(() => {
      // Check if all groups have the same template ID
      const templateIds = fulfillmentGroups.map(
        (group) =>
          group.packingSlipTemplateId ||
          team.settings?.outboundLabels?.packingSlipId,
      );

      if (templateIds.length === 0) {
        return {
          selectedTemplateId: null,
          mixedTemplates: false,
          mixedSizes: false,
        };
      }

      const firstId = templateIds[0];

      if (templateIds.every((id) => id === firstId)) {
        return {
          selectedTemplateId: firstId ?? null,
          mixedTemplates: false,
          mixedSizes: false,
        };
      }

      const selectedTemplates = templates.filter((template) =>
        templateIds.includes(template._id),
      );

      return {
        selectedTemplateId: null,
        mixedTemplates: true,
        mixedSizes: selectedTemplates.some(
          (template) =>
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            template.pageLayout.size !== selectedTemplates[0].pageLayout.size,
        ),
      };
    }, [
      fulfillmentGroups,
      team.settings?.outboundLabels?.packingSlipId,
      templates,
    ]);

    // Create dropdown options from available templates
    const templateOptions = useMemo(
      () =>
        (templates || []).map((template) => ({
          id: template._id,
          text: template.name,
          value: template._id,
          type: "text" as const,
        })),
      [templates],
    );

    // Handle template selection
    const onTemplateChange = useHandler(
      ({ item }: { item: RedoListItem<string> }) => {
        updateRows(
          fulfillmentGroups.map(({ _id }) => ({
            fulfillmentGroupId: _id,
            packingSlipTemplateId: item.value,
          })),
        );
      },
    );

    return (
      <Flex dir="column" gap="xl">
        <RedoSingleSelectDropdownInput
          disabled={everythingDisabled || isEditingLocked}
          options={templateOptions}
          optionSelected={onTemplateChange}
          placeholder={
            mixedTemplates ? "Mixed packing slips" : "Default packing slip"
          }
          selectedItem={templateOptions.find(
            (option) => option.value === selectedTemplateId,
          )}
          size="xs"
        />
        {mixedSizes ? (
          <Text fontSize="xs" textColor="warning">
            Mixed template sizes
          </Text>
        ) : null}
      </Flex>
    );
  },
);
